/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import {
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  Timestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import PennantTeams from "./PennantTeams";

/**
 * Convert a Firestore Timestamp to "DD/MM/YYYY" string.
 */
function formatDateFromTimestamp(timestamp) {
  if (!timestamp || !(timestamp instanceof Timestamp)) return "Unknown Date";
  const dateObj = timestamp.toDate();
  const day = String(dateObj.getDate()).padStart(2, "0");
  const month = String(dateObj.getMonth() + 1).padStart(2, "0");
  const year = dateObj.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * Utility to sort dates in DD/MM/YYYY format.
 */
function sortDates(a, b) {
  const [dA, mA, yA] = a.split("/").map(Number);
  const [dB, mB, yB] = b.split("/").map(Number);
  return new Date(yA, mA - 1, dA) - new Date(yB, mB - 1, dB);
}

/**
 * Predefined weekday order for sorting day headers.
 */
const WEEKDAY_ORDER = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
function sortDays(days) {
  return days.sort((a, b) => {
    const indexA = WEEKDAY_ORDER.indexOf(a);
    const indexB = WEEKDAY_ORDER.indexOf(b);
    if (indexA === -1 && indexB === -1) return a.localeCompare(b);
    if (indexA === -1) return 1;
    if (indexB === -1) return -1;
    return indexA - indexB;
  });
}

/**
 * Groups participants (teams) by their section.
 */
function groupBySectionParticipants(teamsObj) {
  const sectionMap = {};
  Object.values(teamsObj).forEach((tObj) => {
    const sKey = tObj.section || "1";
    if (!sectionMap[sKey]) {
      sectionMap[sKey] = [];
    }
    sectionMap[sKey].push(tObj);
  });
  return sectionMap;
}

export default function Ladder() {
  // ladderData is structured as: { seasonName: { pennantDay: { [pId]: { participants } } } }
  const [ladderData, setLadderData] = useState({});
  // pennantNames: { [pId]: "Sport - Pennant Name" }
  const [pennantNames, setPennantNames] = useState({});
  // datesMap: { [pId]: sorted array of week dates }
  const [datesMap, setDatesMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedSeasons, setExpandedSeasons] = useState({});
  const [expandedDays, setExpandedDays] = useState({}); // keyed by `${seasonName}-${pennantDay}`
  const [expandedPennants, setExpandedPennants] = useState({});
  // (Optional) If you wish to highlight a specific pennant.
  const [pennantData, setPennantData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        // 1) Optionally, load a single known pennant doc.
        const somePennantId = ""; // fill if needed
        if (somePennantId) {
          const pSnap = await getDoc(doc(db, "Pennants", somePennantId));
          if (pSnap.exists()) {
            setPennantData(pSnap.data());
          }
        }

        // 2) Grab all matches from "Matches" (completed matches).
        const matchesSnapshot = await getDocs(query(collection(db, "Matches")));

        // Build a structure: ladderStructure[seasonId][pennantId][displayName]
        const ladderStructure = {};
        const pennantIds = new Set();

        // A) Process each match.
        matchesSnapshot.forEach((docSnap) => {
          const match = docSnap.data();
          const { seasonId, pennantId, team1, team2, section } = match;
          if (!seasonId || !pennantId) return;
          pennantIds.add(pennantId);
          if (!ladderStructure[seasonId]) {
            ladderStructure[seasonId] = {};
          }
          if (!ladderStructure[seasonId][pennantId]) {
            ladderStructure[seasonId][pennantId] = {};
          }
          [team1, team2].forEach((tm) => {
            if (!tm || tm.name === "Bye") return;
            const displayName = tm.teamName || tm.name;
            if (!displayName) return;
            // Use match-level section if available; otherwise, fallback.
            const thisSection = section || tm.section || "1";
            if (!ladderStructure[seasonId][pennantId][displayName]) {
              ladderStructure[seasonId][pennantId][displayName] = {
                name: displayName,
                weeklyPoints: {},
                totalPoints: 0,
                section: thisSection,
              };
            }
            // Use tm.matchPoints (from completed match) to accumulate total points.
            ladderStructure[seasonId][pennantId][displayName].totalPoints +=
              tm.matchPoints || 0;
          });
        });

        // B) For each pennant, fetch its draws subcollection to map weekId -> date.
        const allDatesMap = {}; // { [pId]: Set of dates }
        const weekIdToDate = {}; // mapping: weekId => date string
        const pennantArray = Array.from(pennantIds);
        await Promise.all(
          pennantArray.map(async (pId) => {
            allDatesMap[pId] = new Set();
            const drawsSnap = await getDocs(collection(db, "Pennants", pId, "draws"));
            drawsSnap.forEach((drDoc) => {
              const drData = drDoc.data();
              if (!Array.isArray(drData.weeks)) return;
              drData.weeks.forEach((wk) => {
                if (wk.id && wk.date) {
                  weekIdToDate[wk.id] = wk.date;
                  allDatesMap[pId].add(wk.date);
                }
              });
            });
          })
        );

        // C) Re-iterate matches to add weeklyPoints based on weekId -> date.
        matchesSnapshot.forEach((docSnap) => {
          const match = docSnap.data();
          const { seasonId, pennantId, weekId, team1, team2 } = match;
          if (!seasonId || !pennantId || !weekId) return;
          const dateForThisWeek = weekIdToDate[weekId];
          if (!dateForThisWeek) return;
          [team1, team2].forEach((tm) => {
            if (!tm || tm.name === "Bye") return;
            const displayName = tm.teamName || tm.name;
            const participantObj =
              ladderStructure[seasonId] &&
              ladderStructure[seasonId][pennantId] &&
              ladderStructure[seasonId][pennantId][displayName];
            if (!participantObj) return;
            participantObj.weeklyPoints[dateForThisWeek] =
              (participantObj.weeklyPoints[dateForThisWeek] || 0) +
              (tm.matchPoints || 0);
          });
        });

        // D) Fetch each pennant's info (name, sport, pennantDay).
        const pennantInfoMap = {}; // { pId: { name, pennantDay } }
        const infoPromises = pennantArray.map(async (pId) => {
          const pDoc = await getDoc(doc(db, "Pennants", pId));
          if (pDoc.exists()) {
            const data = pDoc.data();
            const sportPrefix = data.sport ? `${data.sport} - ` : "";
            pennantInfoMap[pId] = {
              name: sportPrefix + (data.name || pId),
              pennantDay: data.pennantDay || "Unknown Day",
            };
            return { pennantId: pId, ...pennantInfoMap[pId] };
          }
          pennantInfoMap[pId] = { name: pId, pennantDay: "Unknown Day" };
          return { pennantId: pId, ...pennantInfoMap[pId] };
        });
        const resolvedInfo = await Promise.all(infoPromises);
        const nameMap = resolvedInfo.reduce((acc, item) => {
          acc[item.pennantId] = item.name;
          return acc;
        }, {});
        setPennantNames(nameMap);

        // E) Convert each allDatesMap[pId] to a sorted array.
        const finalDatesMap = {};
        Object.keys(allDatesMap).forEach((pId) => {
          const dateArr = Array.from(allDatesMap[pId]).sort(sortDates);
          finalDatesMap[pId] = dateArr;
        });
        setDatesMap(finalDatesMap);

        // F) Regroup ladderStructure by season and pennantDay.
        // Final structure: { seasonName: { pennantDay: { [pId]: { participants } } } }
        const ladderBySeasonAndDay = {};
        Object.keys(ladderStructure).forEach((seasonId) => {
          ladderBySeasonAndDay[seasonId] = {};
          const pennantsInSeason = ladderStructure[seasonId];
          Object.keys(pennantsInSeason).forEach((pId) => {
            const penInfo = pennantInfoMap[pId] || { pennantDay: "Unknown Day" };
            const day = penInfo.pennantDay;
            if (!ladderBySeasonAndDay[seasonId][day]) {
              ladderBySeasonAndDay[seasonId][day] = {};
            }
            ladderBySeasonAndDay[seasonId][day][pId] = pennantsInSeason[pId];
          });
        });
        setLadderData(ladderBySeasonAndDay);

        setLoading(false);
      } catch (err) {
        console.error("Error fetching ladder data:", err);
      }
    })();
  }, []);

  // Toggle functions for accordions.
  const toggleSeasonExpand = (seasonId) => {
    setExpandedSeasons((prev) => ({
      ...prev,
      [seasonId]: !prev[seasonId],
    }));
  };

  const toggleDayExpand = (seasonId, day) => {
    const key = `${seasonId}-${day}`;
    setExpandedDays((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const togglePennantExpand = (pennantId) => {
    setExpandedPennants((prev) => ({
      ...prev,
      [pennantId]: !prev[pennantId],
    }));
  };

  if (loading) {
    return <Typography>Loading Ladder...</Typography>;
  }

  return (
    <Box sx={{ padding: 2 }}>
      {/* Optional header if highlighting a particular pennant */}
      {pennantData?.name && (
        <Typography variant="h4" gutterBottom>
          Draws for {pennantData.name}
        </Typography>
      )}
      <Typography variant="h4" gutterBottom>
        Ladder
      </Typography>
      {Object.entries(ladderData).map(([seasonId, daysObj]) => (
        <Box key={seasonId} sx={{ marginBottom: 4 }}>
          {/* Season Header */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              cursor: "pointer",
              bgcolor: "#e0e0e0",
              p: 1,
            }}
            onClick={() => toggleSeasonExpand(seasonId)}
          >
            <Typography variant="h5" gutterBottom>
              Season: {seasonId}
            </Typography>
            <IconButton>
              {expandedSeasons[seasonId] ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
          </Box>
          {expandedSeasons[seasonId] &&
            // Sort the day keys using weekday order.
            sortDays(Object.keys(daysObj)).map((day) => {
              const pennants = daysObj[day];
              const dayKey = `${seasonId}-${day}`;
              // Assume all pennants in a day share the same week dates.
              const theseDates =
                datesMap && datesMap[Object.keys(pennants)[0]]
                  ? datesMap[Object.keys(pennants)[0]]
                  : [];
              return (
                <Box key={day} sx={{ marginBottom: 2, pl: 2 }}>
                  {/* Pennant Day Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      bgcolor: "#f0f0f0",
                      p: 1,
                    }}
                    onClick={() => toggleDayExpand(seasonId, day)}
                  >
                    <Typography variant="h6" gutterBottom>
                      {day}
                    </Typography>
                    <IconButton>
                      {expandedDays[dayKey] ? <ExpandLess /> : <ExpandMore />}
                    </IconButton>
                  </Box>
                  {expandedDays[dayKey] &&
                    Object.entries(pennants).map(([pId, teams]) => {
                      const penName = pennantNames[pId] || pId;
                      return (
                        <Box key={pId} sx={{ marginBottom: 2, pl: 2 }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              cursor: "pointer",
                              bgcolor: "#f9f9f9",
                              p: 1,
                            }}
                            onClick={() => togglePennantExpand(pId)}
                          >
                            <Typography variant="h6" gutterBottom>
                              {penName}
                            </Typography>
                            <PennantTeams pennantId={pId} />
                            <IconButton>
                              {expandedPennants[pId] ? <ExpandLess /> : <ExpandMore />}
                            </IconButton>
                          </Box>
                          {expandedPennants[pId] && (
                            <>
                              {(() => {
                                // Group participants by section.
                                const bySection = groupBySectionParticipants(teams);
                                const sortedSectionKeys = Object.keys(bySection).sort(
                                  (a, b) => Number(a) - Number(b)
                                );
                                return sortedSectionKeys.map((sectionKey) => {
                                  const participants = bySection[sectionKey];
                                  // Sort participants by total points (descending)
                                  participants.sort((a, b) => b.totalPoints - a.totalPoints);
                                  return (
                                    <Box key={sectionKey} sx={{ marginBottom: 4, pl: 3 }}>
                                      <Typography
                                        variant="subtitle1"
                                        gutterBottom
                                        sx={{ bgcolor: "#d0d0ff", p: 0.5, borderRadius: 1 }}
                                      >
                                        Section: {sectionKey}
                                      </Typography>
                                      <TableContainer component={Paper}>
                                        <Table size="small">
                                          <TableHead>
                                            <TableRow>
                                              <TableCell sx={{ p: "2px", minWidth: 0 }}>
                                                Name
                                              </TableCell>
                                              {theseDates.map((dateStr, idx) => (
                                                <TableCell
                                                  key={dateStr}
                                                  align="center"
                                                  sx={{
                                                    bgcolor: idx % 2 === 0 ? "#f0f8ff" : "#f5f5f5",
                                                    p: "2px",
                                                    minWidth: 0,
                                                  }}
                                                >
                                                  {dateStr}
                                                </TableCell>
                                              ))}
                                              <TableCell
                                                align="center"
                                                sx={{
                                                  bgcolor: "#e8e8e8",
                                                  p: "2px",
                                                  minWidth: 0,
                                                }}
                                              >
                                                Total Points
                                              </TableCell>
                                            </TableRow>
                                          </TableHead>
                                          <TableBody>
                                            {participants.map((participant) => (
                                              <TableRow key={participant.name}>
                                                <TableCell sx={{ p: "2px", minWidth: 0 }}>
                                                  {participant.name}
                                                </TableCell>
                                                {theseDates.map((dateStr) => (
                                                  <TableCell
                                                    key={`${participant.name}-${dateStr}`}
                                                    align="center"
                                                    sx={{
                                                      bgcolor: "inherit",
                                                      p: "2px",
                                                      minWidth: 0,
                                                    }}
                                                  >
                                                    {participant.weeklyPoints[dateStr] || 0}
                                                  </TableCell>
                                                ))}
                                                <TableCell align="center" sx={{ p: "2px", minWidth: 0 }}>
                                                  {participant.totalPoints}
                                                </TableCell>
                                              </TableRow>
                                            ))}
                                          </TableBody>
                                        </Table>
                                      </TableContainer>
                                    </Box>
                                  );
                                });
                              })()}
                            </>
                          )}
                        </Box>
                      );
                    })}
                </Box>
              );
            })}
        </Box>
      ))}
    </Box>
  );
}
